// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import apiClient from "../config/api";
import { userService } from "./userService";
import { mapKeycloakRolesToDatabase } from "../config/roleConfig";
import { handleApiCall, cachedApiCall } from "../utils/apiHelper";
import env from "../utils/env";

const SYNC_CACHE_KEY = "user_sync_cache";
const SYNC_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const getSyncCache = (keycloakUserId) => {
  try {
    const cached = sessionStorage.getItem(SYNC_CACHE_KEY);
    if (!cached) return null;

    const cacheData = JSON.parse(cached);
    const userCache = cacheData[keycloakUserId];

    if (!userCache) return null;

    if (Date.now() - userCache.timestamp < SYNC_CACHE_TTL) {
      return userCache.data;
    }

    delete cacheData[keycloakUserId];
    sessionStorage.setItem(SYNC_CACHE_KEY, JSON.stringify(cacheData));
    return null;
  } catch (error) {
    console.error("Error reading sync cache:", error);
    return null;
  }
};

const setSyncCache = (keycloakUserId, data) => {
  try {
    const cached = sessionStorage.getItem(SYNC_CACHE_KEY);
    const cacheData = cached ? JSON.parse(cached) : {};

    cacheData[keycloakUserId] = {
      data,
      timestamp: Date.now(),
    };

    sessionStorage.setItem(SYNC_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error("Error writing sync cache:", error);
  }
};

export const getCachedSyncedUser = (keycloakUserId) =>
  keycloakUserId ? getSyncCache(keycloakUserId) : null;

export const clearSyncCache = () => {
  try {
    sessionStorage.removeItem(SYNC_CACHE_KEY);
  } catch (error) {
    console.error("Error clearing sync cache:", error);
  }
};

export const userSyncService = {
  /**
   * Check if user exists in database (with caching)
   */
  checkUserExists: async (keycloakUserId) => {
    return handleApiCall(
      async () => {
        const response = await apiClient.get(`/admin/users/${keycloakUserId}`);
        return response.data && response.data.user_id ? true : false;
      },
      "Error checking user existence",
      false, // fallback value
    );
  },

  /**
   * Get user by Keycloak ID (with caching)
   */
  getUserByKeycloakId: async (keycloakUserId) => {
    const cacheKey = `user:${keycloakUserId}`;
    const cacheTTL = 10 * 60 * 1000;

    return cachedApiCall(
      cacheKey,
      async () => {
        const response = await apiClient.get(`/admin/users/${keycloakUserId}`);
        return response.data;
      },
      cacheTTL,
    ).catch((error) => {
      console.error("Error fetching user by Keycloak ID:", error);
      return null;
    });
  },

  /**
   * Create user
   */
  createUser: async (userData) => {
    return handleApiCall(
      () => userService.postTenantUser(userData),
      "Error creating user",
    );
  },

  /**
   * Update user roles
   */
  updateUserRoles: async (userId, roles) => {
    return handleApiCall(
      () => userService.putTenantUser(userId, { roles }),
      "Error updating user roles",
    );
  },

  /**
   * Map Keycloak roles to database roles
   */
  mapKeycloakRolesToDatabase: (keycloakRoles) => {
    return mapKeycloakRolesToDatabase(keycloakRoles);
  },

  /**
   * Sync user with database (optimized with caching)
   */
  syncUser: async (keycloakUserInfo) => {
    const {
      id: keycloakUserId,
      username,
      email,
      firstName,
      lastName,
      resourceRoles,
    } = keycloakUserInfo;

    const cachedSync = getSyncCache(keycloakUserId);
    if (cachedSync) return cachedSync;

    return handleApiCall(async () => {
      const userExists = await userSyncService.checkUserExists(keycloakUserId);
      const resourceName = env.REACT_APP_KEYCLOAK_CLIENT_ID;
      const keycloakRoles = resourceRoles?.[resourceName]?.roles || [];
      const mappedRoles =
        userSyncService.mapKeycloakRolesToDatabase(keycloakRoles);

      if (userExists) {
        const dbUser =
          await userSyncService.getUserByKeycloakId(keycloakUserId);
        const currentRolesSorted = (dbUser.roles || []).sort();
        const newRolesSorted = [...mappedRoles].sort();

        if (
          JSON.stringify(currentRolesSorted) !== JSON.stringify(newRolesSorted)
        ) {
          await userSyncService.updateUserRoles(dbUser.user_id, mappedRoles);
          const syncedData = { ...dbUser, roles: mappedRoles, updated: true };
          setSyncCache(keycloakUserId, syncedData);
          return syncedData;
        }

        const syncedData = { ...dbUser, updated: false };
        setSyncCache(keycloakUserId, syncedData);
        return syncedData;
      }

      const newUser = await userSyncService.createUser({
        keycloak_id: keycloakUserId,
        username,
        email,
        first_name: firstName,
        last_name: lastName,
        roles: mappedRoles,
        status: "active",
      });
      const syncedData = { ...newUser, created: true };
      setSyncCache(keycloakUserId, syncedData);
      return syncedData;
    }, "Error synchronizing user");
  },
};
